// src/pages/orcamentos/OrcamentoPage.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FaCheck, FaFilePdf, FaPlus, FaTimes, FaTrash } from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import Input from '../../components/input/Input';
import { useFilial } from '../../contexts/administrativo/FilialContext';
import { useYesNo } from '../../contexts/geral/YesNoContext';
import { useMessage } from '../../contexts/geral/MessageContext';
import { useUser } from '../../contexts/geral/UserContext';
import { useOrcamento } from '../../contexts/orcamentos/OrcamentoContext';
import { useCliente } from '../../contexts/pessoas/ClienteContext';
import { useConfigWhats } from '../../contexts/pedidos/ConfigWhatsContext';
import { useEquipamentoCliente } from '../../contexts/servicos/EquipamentoClienteContext';
import { Orcamento, moedaOrcamento, SITUACOES_ORCAMENTO } from '../../models/orcamentos/Orcamento';
import { parseNumeric } from '../../utils/numberUtils';
import OrcamentoItemModal from './OrcamentoItemModal';
import OrcamentoReprovarModal from './OrcamentoReprovarModal';
import '../servicos/ordensServico/Servicos.css';

const OrcamentoPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useOrcamento();
  const { filialLogin } = useFilial();
  const { user } = useUser();
  const { showMessage } = useMessage();
  const { showYesNo } = useYesNo();
  const { configWhats } = useConfigWhats();
  const { clientes, showClienteSearch, showClienteCad } = useCliente();
  const { equipamentos, listarEquipamentos, handleShowEquipamentoClienteSearch, showEquipamentoClienteCad } = useEquipamentoCliente();
  const [orcamento, setOrcamento] = useState(new Orcamento());
  const [showItem, setShowItem] = useState(false);
  const [itemEdicao, setItemEdicao] = useState(null);
  const [indiceEdicao, setIndiceEdicao] = useState(null);
  const [showReprovar, setShowReprovar] = useState(false);
  const carregadoRef = useRef(null);
  const aberto = Number(orcamento.situacao) === 1;

  useEffect(() => {
    const carregar = async () => {
      if (id) {
        if (carregadoRef.current === id) return;
        carregadoRef.current = id;
        const encontrado = await api.obter(id);
        if (encontrado) setOrcamento(encontrado);
      } else {
        carregadoRef.current = null;
        setOrcamento(new Orcamento(filialLogin?.id, user?.id));
      }
    };
    carregar();
  }, [id, filialLogin?.id, user?.id]);

  useEffect(() => { if (orcamento.clienteId) listarEquipamentos(orcamento.clienteId, true); }, [orcamento.clienteId]);
  const alterar = (campo, valor) => setOrcamento(anterior => ({ ...anterior, [campo]: valor }));
  const totalItens = useMemo(() => orcamento.itens.reduce((soma, item) => soma + Number(item.quantidade) * Number(item.valorUnitario) - Number(item.valorDesconto || 0), 0), [orcamento.itens]);
  const total = Math.max(totalItens - parseNumeric(orcamento.valorDesconto), 0);

  const selecionarCliente = cliente => {
    if (!cliente?.id) return;
    setOrcamento(anterior => ({ ...anterior, clienteId: cliente.id, clienteNome: cliente.pessoa?.nome || cliente.nome || '', clienteTelefone: cliente.pessoa?.telefone?.numero || '', equipamentoClienteId: null, equipamentoDescricao: '' }));
  };
  const validarCliente = clienteId => {
    if (!clienteId) { alterar('clienteId', 0); alterar('clienteNome', ''); return; }
    const cliente = clientes.find(x => Number(x.id) === Number(clienteId));
    if (cliente) selecionarCliente(cliente);
    else { showMessage('Cliente não encontrado.', 'warning'); alterar('clienteId', 0); alterar('clienteNome', ''); }
  };
  const buscarCliente = async () => selecionarCliente(await showClienteSearch());
  const cadastrarCliente = async () => selecionarCliente(await showClienteCad(null, false));
  const buscarEquipamento = async () => {
    if (!orcamento.clienteId) { showMessage('Informe o cliente antes de selecionar o equipamento.', 'warning'); return; }
    const equipamento = await handleShowEquipamentoClienteSearch(orcamento.clienteId);
    if (equipamento?.id) setOrcamento(anterior => ({ ...anterior, equipamentoClienteId: equipamento.id, equipamentoDescricao: equipamento.descricao }));
  };
  const validarEquipamento = equipamentoId => {
    if (!equipamentoId) { alterar('equipamentoClienteId', null); alterar('equipamentoDescricao', ''); return; }
    const equipamento = equipamentos.find(x => Number(x.id) === Number(equipamentoId));
    if (equipamento) setOrcamento(anterior => ({ ...anterior, equipamentoClienteId: equipamento.id, equipamentoDescricao: equipamento.descricao }));
    else { showMessage('Equipamento não encontrado para o cliente.', 'warning'); alterar('equipamentoClienteId', null); }
  };
  const cadastrarEquipamento = async () => {
    if (!orcamento.clienteId) { showMessage('Informe o cliente antes de cadastrar o equipamento.', 'warning'); return; }
    const cliente = clientes.find(x => Number(x.id) === Number(orcamento.clienteId)) || { id: orcamento.clienteId, pessoa: { nome: orcamento.clienteNome } };
    const equipamento = await showEquipamentoClienteCad(null, cliente, true);
    if (equipamento?.id) setOrcamento(anterior => ({ ...anterior, equipamentoClienteId: equipamento.id, equipamentoDescricao: equipamento.descricao }));
  };

  const payload = dados => ({
    filialId: dados.filialId,
    clienteId: Number(dados.clienteId),
    equipamentoClienteId: dados.equipamentoClienteId ? Number(dados.equipamentoClienteId) : null,
    problemaRelatado: dados.problemaRelatado || null,
    observacao: dados.observacao || null,
    validadeDias: Number(dados.validadeDias || 15),
    valorDesconto: parseNumeric(dados.valorDesconto),
    usuarioId: user?.id,
    itens: dados.itens.map(item => ({ id: item.id || 0, produtoId: Number(item.produtoId), xProd: item.xProd, quantidade: Number(item.quantidade), valorUnitario: Number(item.valorUnitario), valorDesconto: Number(item.valorDesconto || 0), observacao: item.observacao || null })),
  });

  const salvar = async () => {
    if (!orcamento.clienteId) { showMessage('Informe o cliente.', 'warning'); return null; }
    if (!orcamento.itens.length) { showMessage('Inclua ao menos um produto ou serviço.', 'warning'); return null; }
    const salvo = orcamento.id ? await api.atualizar(orcamento.id, payload(orcamento)) : await api.criar(payload(orcamento));
    if (!salvo) return null;
    setOrcamento(salvo);
    if (!orcamento.id) navigate(`/orcamento/${salvo.id}`, { replace: true });
    if (!orcamento.id) {
      const desejaEnviarWhatsApp = salvo.clienteTelefone && !configWhats?.desativarWhats
        ? await showYesNo('Deseja enviar o orçamento para o cliente pelo WhatsApp?', 'yes')
        : false;

      if (desejaEnviarWhatsApp) {
        await api.enviarWhatsApp(salvo.id);
      } else {
        await api.imprimir(salvo.id);
      }
    }
    return salvo;
  };
  const salvarItem = item => {
    alterar('itens', indiceEdicao === null ? [...orcamento.itens, item] : orcamento.itens.map((atual, indice) => indice === indiceEdicao ? item : atual));
    setShowItem(false); setItemEdicao(null); setIndiceEdicao(null);
  };
  const removerItem = async indice => {
    if (await showYesNo('Deseja excluir este item do orçamento?', 'no')) alterar('itens', orcamento.itens.filter((_, i) => i !== indice));
  };
  const aprovar = async () => {
    const salvo = await salvar();
    if (!salvo || !await showYesNo('Deseja aprovar o orçamento e gerar uma ordem de serviço aberta?', 'no')) return;
    const resultado = await api.aprovar(salvo.id, { usuarioId: user?.id });
    if (resultado?.ordemServicoId) navigate(`/ordemServico/${resultado.ordemServicoId}`);
  };
  const reprovar = async motivo => {
    const resultado = await api.reprovar(orcamento.id, { usuarioId: user?.id, motivo });
    if (resultado) { setOrcamento(resultado); setShowReprovar(false); }
  };

  return <div className="servicos-page container-fluid py-3">
    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
      <h3>{orcamento.id ? `Orçamento nº ${String(orcamento.numero).padStart(6, '0')}` : 'Novo orçamento'}</h3>
      <span className={`badge ${aberto ? 'bg-warning text-dark' : Number(orcamento.situacao) === 2 ? 'bg-success' : 'bg-danger'}`}>{SITUACOES_ORCAMENTO[orcamento.situacao]}</span>
    </div>
    <section className="servicos-card">
      <div className="d-flex flex-wrap gap-2 align-items-end">
        <Input label="Cliente" value={orcamento.clienteId || ''} disabled={!aberto} size={100} btnBuscar={aberto ? buscarCliente : null} btnAdd={aberto ? cadastrarCliente : null} onChange={e => alterar('clienteId', e.target.value.replace(/\D/g, ''))} onBlur={e => validarCliente(e.target.value)} />
        <Input label="Nome do cliente" value={orcamento.clienteNome || ''} disabled size={300} />
        <Input label="Equipamento (opcional)" value={orcamento.equipamentoClienteId || ''} disabled={!aberto} size={120} btnBuscar={aberto ? buscarEquipamento : null} btnAdd={aberto ? cadastrarEquipamento : null} onChange={e => alterar('equipamentoClienteId', e.target.value.replace(/\D/g, ''))} onBlur={e => validarEquipamento(e.target.value)} />
        <Input label="Descrição do equipamento" value={orcamento.equipamentoDescricao || ''} disabled size={260} />
        <Input label="Validade (dias)" type="number" value={orcamento.validadeDias} disabled={!aberto} size={110} onChange={e => alterar('validadeDias', e.target.value)} />
      </div>
      <div className="mt-3"><Input label="Problema relatado (opcional)" value={orcamento.problemaRelatado || ''} disabled={!aberto} rows={3} size="100%" onChange={e => alterar('problemaRelatado', e.target.value)} /></div>
      <div className="mt-3"><Input label="Observações" value={orcamento.observacao || ''} disabled={!aberto} rows={3} size="100%" onChange={e => alterar('observacao', e.target.value)} /></div>
    </section>
    <section className="servicos-card mt-3">
      <div className="d-flex justify-content-between mb-2"><h5>Produtos e serviços</h5>{aberto && <button type="button" className="btn btn-primary" onClick={() => { setItemEdicao(null); setIndiceEdicao(null); setShowItem(true); }}><FaPlus /> Adicionar</button>}</div>
      <div className="table-responsive"><table className="table table-hover"><thead><tr><th>Descrição</th><th className="text-end">Qtd.</th><th className="text-end">Unitário</th><th className="text-end">Desconto</th><th className="text-end">Total</th>{aberto && <th />}</tr></thead><tbody>
        {orcamento.itens.map((item, indice) => <tr key={item.id || indice} onDoubleClick={() => { if (aberto) { setItemEdicao(item); setIndiceEdicao(indice); setShowItem(true); } }}><td>{item.xProd}</td><td className="text-end">{Number(item.quantidade).toLocaleString('pt-BR')}</td><td className="text-end">{moedaOrcamento(item.valorUnitario)}</td><td className="text-end">{moedaOrcamento(item.valorDesconto)}</td><td className="text-end">{moedaOrcamento(Number(item.quantidade) * Number(item.valorUnitario) - Number(item.valorDesconto || 0))}</td>{aberto && <td className="text-end"><button type="button" className="btn btn-sm btn-outline-danger" onClick={() => removerItem(indice)}><FaTrash /></button></td>}</tr>)}
        {!orcamento.itens.length && <tr><td colSpan="6" className="text-center text-muted">Nenhum item informado.</td></tr>}
      </tbody></table></div>
      <div className="d-flex justify-content-end align-items-end gap-3"><Input label="Desconto geral" value={orcamento.valorDesconto || 0} disabled={!aberto} size={130} onChange={e => alterar('valorDesconto', e.target.value)} /><h4>Total: {moedaOrcamento(total)}</h4></div>
    </section>
    <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
      <button type="button" className="btn btn-secondary" onClick={() => navigate('/orcamento/consultar')}>Voltar</button>
      {orcamento.id && <button type="button" className="btn btn-outline-primary" onClick={() => api.imprimir(orcamento.id)}><FaFilePdf /> PDF</button>}
      {aberto && <button type="button" className="btn btn-primary" disabled={api.loading} onClick={salvar}>Salvar</button>}
      {aberto && orcamento.id && <button type="button" className="btn btn-danger" onClick={() => setShowReprovar(true)}><FaTimes /> Reprovar</button>}
      {aberto && <button type="button" className="btn btn-success" disabled={api.loading} onClick={aprovar}><FaCheck /> Aprovar e gerar OS</button>}
      {Number(orcamento.situacao) === 2 && orcamento.ordemServicoGeradaId && <button type="button" className="btn btn-success" onClick={() => navigate(`/ordemServico/${orcamento.ordemServicoGeradaId}`)}>Abrir ordem de serviço</button>}
    </div>
    <OrcamentoItemModal show={showItem} item={itemEdicao} onHide={() => setShowItem(false)} onSave={salvarItem} />
    <OrcamentoReprovarModal show={showReprovar} onHide={() => setShowReprovar(false)} onConfirm={reprovar} />
  </div>;
};

export default OrcamentoPage;
